/**
 * Sewing patterns referential: the "BDP n" names a model can carry, listed by
 * number then by name.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import { sortByKey } from '@/shared/lib/sort';
import {
  namedItemSchema,
  type NamedItem,
  type NamedItemService,
} from '../named-items/api';

export type SewingPattern = NamedItem;

/** The number of a "BDP 42" name, undefined for any other shape. */
export const bdpNumber = (name: string): number | undefined => {
  const parts = name.split(' ');
  if (parts.length !== 2) return undefined;
  const [prefix, number] = parts;
  const parsed = Number(number);
  return prefix === 'BDP' && Number.isInteger(parsed) ? parsed : undefined;
};

/** By BDP number, the other names after them by name. */
export const sortSewingPatterns = (
  patterns: readonly SewingPattern[],
): SewingPattern[] =>
  sortByKey(
    sortByKey(patterns, (pattern) => pattern.name),
    (pattern) => bdpNumber(pattern.name) ?? Number.POSITIVE_INFINITY,
  );

export const sewingPatternsQueryKey = ['sewing-patterns'] as const;

const listSewingPatterns = async () =>
  sortSewingPatterns(
    await api.get<SewingPattern[]>('sewing-pattern', {
      schema: z.array(namedItemSchema),
    }),
  );

export const sewingPatternsQueryOptions = () =>
  queryOptions({
    queryKey: sewingPatternsQueryKey,
    queryFn: listSewingPatterns,
    retry: false,
  });

export const sewingPatternService: NamedItemService = {
  label: 'sewing pattern',
  queryKey: sewingPatternsQueryKey,
  list: listSewingPatterns,
  create: (name) => api.post<SewingPattern>('sewing-pattern', { name }),
  update: (id, name) =>
    api.put<SewingPattern>(`sewing-pattern/${encodeURIComponent(id)}`, {
      name,
    }),
  delete: (id) =>
    api.delete<unknown>(`sewing-pattern/${encodeURIComponent(id)}`),
};
